import React, { FC, useCallback, useLayoutEffect, useMemo, useState, useEffect } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Form } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { contractOptionsActions, getContractOptions, UnitType, useContractOptions } from 'entities/Contract';
import { ModernRadioButtonGroup, RadioOptionTheme } from 'shared/ui/ModernRadioButtonGroup';
import { useCheckUserDataComplete, UserAccountType, User, getLoggedUserData, useAuthorizedState } from 'entities/User';
import { useScrollToElement } from 'shared/utils/hooks/useScrollToElement';
import { Box } from 'entities/Box';
import { WarehouseForBooking } from 'entities/Warehouse';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { ModernDatePicker } from 'shared/ui/ModernDatePicker';
import { ModernSelect } from 'shared/ui/ModernSelect';
import { InfoTag } from 'shared/ui/InfoTag';
import { useGetCurrencySymbol } from 'app/appState';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { PaymentFrequencySetting } from 'entities/RentOption';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { Toggle } from 'shared/ui/Toggle';
import { ModernInput } from 'shared/ui/ModernInput';
import { Button } from 'shared/ui/Button';
import { Checkbox } from 'shared/ui/Checkbox';
import { ReserveBox } from 'features/ReserveBox';
import { MobileBookingInfoStack } from 'widgets/MobileBookingInfoStack';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

interface ModernContractDetailsMobileProps {
  selectedWarehouse: Nullable<WarehouseForBooking>;
  boxInfo: Nullable<Box>;
  userInfo: Nullable<User>;
  userAccountType: UserAccountType;
  selectedWarehouseId: string | undefined;
  isValidForm: boolean;
  onReservationSuccess: () => void;
  goToNextStep: () => void;
}

export const ModernContractDetailsMobile: FC<ModernContractDetailsMobileProps> = (props) => {
  const { selectedWarehouse, boxInfo, userInfo, userAccountType, isValidForm, onReservationSuccess, goToNextStep } = props;

  const { t } = useAppTranslation('booking');
  const dispatch = useAppDispatch();

  const currencySymbol = useGetCurrencySymbol();

  const { scrollToElement } = useScrollToElement('center', 500);

  useLayoutEffect(() => {
    scrollToElement();
  }, [scrollToElement]);

  const isUserDataCompleted = useCheckUserDataComplete();
  const isAuthorized = useAuthorizedState();
  const loggedUser = useAppSelector(getLoggedUserData);
  const contractOptions = useAppSelector(getContractOptions);
  const needAuthorization = !isAuthorized || !isUserDataCompleted;

  // Account type logic
  const accountTypeOptions = [
    { value: UserAccountType.PERSONAL, label: t('Personal'), theme: RadioOptionTheme.CLEAR },
    { value: UserAccountType.BUSINESS, label: t('Business'), theme: RadioOptionTheme.CLEAR },
  ];

  const changeAccountType = useCallback(
    (accountType: UserAccountType): void => {
      dispatch(contractOptionsActions.setContractOptions({ accountType }));
    },
    [dispatch],
  );

  const {
    contractOptions: contractOptionsFromHook,
    insuranceOptions,
    availableRentOptions,
    changeRentOption,
    handleEndDateChange,
    disabledEndDate,
    handleInsuranceChange,
    disabledStartDate,
    handleEntirePaymentChange,
    handleStartDateChange,
    firstAvailableEndDate,
    firstAvailableStartDate,
    useUserBalance,
    setUseUserBalance,
    setPromoCodeValue,
    promoDiscounts,
    isLoading,
  } = useContractOptions({
    selectedWarehouse,
    box: boxInfo,
    user: userInfo,
    userAccountType,
  });

  const contractDurationOptions = useMemo(() => {
    return availableRentOptions.map((option) => ({
      value: option.rentOptionId,
      label: getLocalizedString(option.label),
    }));
  }, [availableRentOptions]);

  const changeContractDuration = useCallback(
    (rentOptionId: string): void => {
      const selectedOption = availableRentOptions.find((option) => option.rentOptionId === rentOptionId);
      if (selectedOption) {
        changeRentOption(selectedOption);
      }
    },
    [availableRentOptions, changeRentOption],
  );

  const isReserveDisabled = useMemo<boolean>(() => {
    const insuranceNeeded = boxInfo?.unitType === UnitType.BOX && Boolean(insuranceOptions?.length);
    let isDisabled =
      !isUserDataCompleted ||
      !selectedWarehouse ||
      !boxInfo ||
      !contractOptions.startDate ||
      (insuranceNeeded && !contractOptions.insuranceId);

    if (contractOptions.invoiceFrequencyType === 'day') {
      isDisabled = isDisabled || !contractOptions.endDate;
    }

    return isDisabled;
  }, [
    boxInfo,
    contractOptions.endDate,
    contractOptions.insuranceId,
    contractOptions.invoiceFrequencyType,
    contractOptions.startDate,
    isUserDataCompleted,
    selectedWarehouse,
    insuranceOptions,
  ]);

  // Promo code logic
  const [promoCode, setPromoCode] = useState<string>('');
  const [hasPromoCode, setHasPromoCode] = useState<boolean>(false);

  const isPromoApplied = useMemo(() => {
    return Boolean(promoDiscounts?.length);
  }, [promoDiscounts]);

  const isWrongPromo = useMemo(() => {
    return Boolean(contractOptionsFromHook?.promoCodeValue && !isPromoApplied);
  }, [contractOptionsFromHook?.promoCodeValue, isPromoApplied]);

  // Auto-check promo code checkbox when promo is applied
  useEffect(() => {
    if (isPromoApplied && !hasPromoCode) {
      setHasPromoCode(true);
    }
  }, [isPromoApplied, hasPromoCode]);

  return (
    <div className="flex flex-col min-h-[100dvh] bg-backgroundColor">
      {/* First Part: Scrollable Content with White Background */}
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-1">
          {/* Mobile Booking Info Stack */}
          {selectedWarehouse && (
            <div className="bg-white rounded-lg overflow-hidden">
              <MobileBookingInfoStack warehouseInfo={selectedWarehouse} boxInfo={boxInfo} contractOptions={contractOptions} />
            </div>
          )}

          {/* Main Form Card */}
          <div className="bg-white rounded-lg p-4 space-y-4">
            {/* Header Row: Booking title and Account Type selector */}
            <div className="flex flex-col justify-between">
              <div className="font-semibold text-24 text-primary font-tektur">{t('Booking')}</div>
              <div className="mt-4 w-[250px]">
                <ModernRadioButtonGroup
                  options={accountTypeOptions}
                  value={userAccountType}
                  onChange={changeAccountType}
                  isDisabled={isUserDataCompleted}
                />
              </div>
            </div>

            <div>
              {/* Mobile Layout: Single Column */}
              <div className="space-y-6 mb-6">
                {/* Contract Duration (Rent Options) */}
                <div>
                  <div className="font-semibold text-lg font-tektur mb-1">{t('Tariffs')}</div>
                  <div>
                    {isLoading ? (
                      <div className="space-y-3">
                        <div className="h-6 bg-secondaryLight rounded w-1/2" />
                        <div className="h-6 bg-secondaryLight rounded w-2/3" />
                        <div className="h-6 bg-secondaryLight rounded w-1/3" />
                      </div>
                    ) : (
                      <ModernRadioButtonGroup
                        options={contractDurationOptions}
                        direction="vertical"
                        withBorder
                        onChange={changeContractDuration}
                        value={contractOptionsFromHook?.rentOption?.rentOptionId}
                        labelPosition="post"
                        optionType="default"
                      />
                    )}
                  </div>
                  {!isLoading && contractOptionsFromHook?.rentOption?.description ? (
                    <div className="text-sm text-primaryLight mt-4">
                      {getLocalizedString(contractOptionsFromHook.rentOption.description)}
                    </div>
                  ) : null}
                </div>

                {/* Move-in Date and Coverage - Single Column Layout */}
                <div className="space-y-4">
                  {/* Move-in Date */}
                  <div>
                    <div className="font-semibold text-lg font-tektur mb-1">{t('Move-in date')}</div>
                    {isLoading ? (
                      <div className="h-14 bg-secondaryLight rounded w-full" />
                    ) : (
                      <ModernDatePicker
                        className="w-full"
                        format={CLIENT_DATE_FORMAT}
                        onChange={handleStartDateChange}
                        placeholder={t(CLIENT_DATE_FORMAT)}
                        inputReadOnly
                        disabledDate={disabledStartDate}
                        value={contractOptionsFromHook.startDate}
                        defaultPickerValue={firstAvailableStartDate}
                      />
                    )}
                  </div>

                  {/* Move-out Date (conditional) */}
                  {contractOptionsFromHook.invoiceFrequencyType === InvoiceFrequencyType.DAY && (
                    <div>
                      <div className="font-semibold text-lg font-tektur mb-1">{t('Move-out date')}</div>
                      {isLoading ? (
                        <div className="h-14 bg-secondaryLight rounded w-full" />
                      ) : (
                        <ModernDatePicker
                          format={CLIENT_DATE_FORMAT}
                          className="w-full"
                          onChange={handleEndDateChange}
                          placeholder={t(CLIENT_DATE_FORMAT)}
                          disabled={!contractOptionsFromHook.startDate}
                          inputReadOnly
                          requiredMark
                          disabledDate={disabledEndDate}
                          showToday={false}
                          value={contractOptionsFromHook.endDate}
                          defaultPickerValue={firstAvailableEndDate}
                        />
                      )}
                    </div>
                  )}

                  {/* Insurance */}
                  <div>
                    <div className="font-semibold text-lg font-tektur mb-1">{t('Coverage amount')}</div>
                    <Form.Item name="insuranceId">
                      {!insuranceOptions?.length ? (
                        <InfoTag>{t('No insurance provided!')}</InfoTag>
                      ) : (
                        <div className="w-full">
                          <ModernSelect
                            className="w-full"
                            placeholder={t('Select')}
                            onChange={handleInsuranceChange}
                            options={insuranceOptions}
                            value={contractOptionsFromHook.insuranceId}
                          />
                          {contractOptionsFromHook.insuranceId && (
                            <div className="text-sm mt-2 pl-1">
                              {t(`{{rate}} {{currencySymbol}}/${contractOptionsFromHook.invoiceFrequencyType}`, {
                                rate: formatPrice(contractOptionsFromHook.insuranceRate),
                                currencySymbol,
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </Form.Item>
                  </div>
                </div>
              </div>

              {/* Promo Code */}
              <div className="mb-6">
                <div className="flex items-center mb-3">
                  <Checkbox
                    name="hasPromoCode"
                    checked={hasPromoCode}
                    disabled={isPromoApplied}
                    onChange={(checked) => {
                      if (!isPromoApplied) {
                        setHasPromoCode(checked);
                        if (!checked) {
                          setPromoCode('');
                          setPromoCodeValue('');
                        }
                      }
                    }}
                  />
                  <span className="ml-2 font-semibold text-base">{t('Have a promo code ?')}</span>
                </div>

                {hasPromoCode && (
                  <div>
                    {isPromoApplied && <div className="font-semibold text-success mb-1">{t('Promo has been successfully applied')}</div>}
                    {isWrongPromo && <div className="font-semibold text-error mb-1">{t('Wrong promo code, please try another one')}</div>}
                    <div className="flex items-center">
                      <ModernInput
                        onChange={setPromoCode}
                        value={promoCode}
                        containerClassName="w-full max-w-[200px]"
                        className="rounded-tr-none rounded-br-none"
                        placeholder={t('Promo code')}
                        disabled={isPromoApplied}
                        bordered={false}
                        uppercase
                      />
                      <div className="transform -translate-x-[5px]">
                        <Button
                          isDisabled={isPromoApplied}
                          theme="dark"
                          onClick={() => {
                            setPromoCodeValue(promoCode);
                          }}
                        >
                          {t('Apply')}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Balance and Prepayment */}
              <div className="flex gap-6 flex-col space-y-4">
                {Boolean(userInfo?.balance) && (
                  <div className="flex-1">
                    <InfoTag textSize="medium">
                      {t('You have {{balance}} {{currencySymbol}} on your balance, you can use it to pay your booking', {
                        balance: formatPrice(userInfo?.balance),
                        currencySymbol,
                      })}
                    </InfoTag>
                    <div className="font-semibold text-base mt-4 mb-1">{t('Personal balance')}</div>
                    <div className="flex items-center w-full max-w-[580px] rounded-lg p-2 border border-secondaryAccent">
                      <Toggle checked={useUserBalance} onChange={setUseUserBalance} />
                      <div className="text-m">{t('Use your balance')}</div>
                    </div>
                  </div>
                )}
                {contractOptionsFromHook?.rentOption?.paymentFrequencySetting === PaymentFrequencySetting.ALL && (
                  <div className="flex-1">
                    <InfoTag textSize="medium">{t('Discount information for entire period!')}</InfoTag>
                    <div className="font-semibold text-base mt-4 mb-1">{t('Prepayment')}</div>
                    <div className="flex items-center w-full max-w-[580px] rounded-lg p-2 border border-secondaryAccent">
                      <Toggle checked={contractOptionsFromHook.payForEntirePeriod} onChange={handleEntirePaymentChange} />
                      <div className="text-m">{t('Pay for the entire period')}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Second Part: Fixed Action Buttons with backgroundColor background */}
      <div className="bg-backgroundColor py-4 flex-shrink-0">
        <div className="flex space-x-2">
          {!needAuthorization && (
            <ReserveBox
              action={
                <Button containerClassName="w-full" size="medium" theme="primary-outlined" isDisabled={isLoading || isReserveDisabled}>
                  {t('Reserve')}
                </Button>
              }
              boxId={boxInfo?.boxId}
              warehouseId={selectedWarehouse?.warehouseId}
              contractOptions={contractOptions}
              userId={loggedUser?.userId}
              onSuccess={onReservationSuccess}
              needConfirmBookingDocs
            />
          )}
          <Button containerClassName="w-full" size="medium" type="submit" isDisabled={isLoading || !isValidForm} onClick={goToNextStep}>
            {t('Continue')}
          </Button>
        </div>
      </div>
    </div>
  );
};
